"use client"

import { useEffect } from "react"
import { m, AnimatePresence } from "framer-motion"
import { FilloutEmbed } from "@/components/ui/fillout-embed"

interface BookCallModalProps {
  open: boolean
  onClose: () => void
  /** Fillout form ID to render inside the modal. */
  formId: string
  /** Optional ARIA label override (default: "Book a discovery call"). */
  ariaLabel?: string
}

export function BookCallModal({
  open,
  onClose,
  formId,
  ariaLabel = "Book a discovery call",
}: BookCallModalProps) {
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.body.style.overflow = "hidden"
    window.addEventListener("keydown", handler)
    return () => {
      document.body.style.overflow = ""
      window.removeEventListener("keydown", handler)
    }
  }, [open, onClose])

  return (
    // safe-animate-presence: conditional-mount — child only renders when `open=true`
    <AnimatePresence>
      {open && (
        <>
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <m.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            className="fixed inset-3 z-[101] flex flex-col overflow-hidden bg-white md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2"
            style={{
              borderRadius: 24,
              maxWidth: 860,
              width: "calc(100% - 24px)",
              maxHeight: "92vh",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 z-10 flex cursor-pointer items-center justify-center transition-colors hover:bg-neutral-100"
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                background: "var(--color-neutral-100)",
              }}
              aria-label="Close"
              autoFocus
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="rgb(100,100,100)"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M2 2l10 10M12 2L2 12" />
              </svg>
            </button>
            <div className="flex-1 overflow-y-auto">
              <FilloutEmbed formId={formId} />
            </div>
          </m.div>
        </>
      )}
    </AnimatePresence>
  )
}
